// CONNECT 2.0 data-testid convention helpers (design-system foundation, Spec 2
// Task 9). Pure functions — no Angular dependency — so they can be used from
// component TS, templates (via a component method), or plain unit tests.
//
// See `src/app/shared/testid/README.md` for the full convention this backs:
// singleton `<area>-<control>` ids and repeated-row flat-composite ids like
// `site-002-product-pca-submit-quote`.

/**
 * Slugify a string into the kebab-case form the data-testid convention
 * requires:
 *   1. lowercase
 *   2. any run of non-alphanumeric characters → a single `-`
 *   3. collapse repeated `-`
 *   4. trim leading/trailing `-`
 *
 * Never derive a testid from user-visible copy directly — copy changes over
 * time and would silently break automation. Use `slugify` only on stable,
 * non-presentational identifiers (a product code, a status key, etc.).
 *
 * @example
 * slugify('ASTM E1527-21')      // 'astm-e1527-21'
 * slugify('  Site Visit Only ') // 'site-visit-only'
 * slugify('PCA')                // 'pca'
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Factory for a dedupe-aware id builder, scoped to one caller (e.g. one
 * table/list render pass). Repeated rows can legitimately produce the same
 * base id (two products slugifying to the same string on the same site); the
 * convention resolves that by suffixing `-2`, `-3`, ... on collision.
 *
 * Each scope tracks its own seen-id counts, so create a fresh scope per
 * render pass (e.g. once per `*ngFor`/table build) rather than sharing one
 * globally — a shared scope would keep incrementing across unrelated renders.
 *
 * @example
 * const scope = createTestIdScope();
 * scope.next('site-002-product-pca-submit-quote'); // 'site-002-product-pca-submit-quote'
 * scope.next('site-002-product-pca-submit-quote'); // 'site-002-product-pca-submit-quote-2'
 * scope.next('site-002-product-pca-submit-quote'); // 'site-002-product-pca-submit-quote-3'
 */
export function createTestIdScope(): { next: (id: string) => string } {
  const seen = new Map<string, number>();

  return {
    next(id: string): string {
      const count = (seen.get(id) ?? 0) + 1;
      seen.set(id, count);
      return count === 1 ? id : `${id}-${count}`;
    },
  };
}

/**
 * Convenience wrapper combining the two: slugify each part, join with `-`,
 * and dedupe within the given scope. Use this to build a repeated-row
 * composite id in one call, e.g.:
 *
 *   buildTestId(scope, 'site', '002', 'product', productCode, 'submit-quote')
 *   // 'site-002-product-pca-submit-quote' (or '-2'/'-3' on collision)
 */
export function buildTestId(
  scope: { next: (id: string) => string },
  ...parts: string[]
): string {
  const id = parts
    .map((part) => slugify(part))
    .filter((part) => part.length > 0)
    .join('-');
  return scope.next(id);
}
